import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Temporal } from '@js-temporal/polyfill';
import { finalize, take } from 'rxjs';

import { ReportService } from '@proxy/reports';
import { GetWinesSalesReportDto, WineSalesReportDto } from '@proxy/reports/dto';
import { Column } from 'src/app/shared/components/metro-table/metro-table.component';

@Component({
  selector: 'app-wine-sales-report',
  templateUrl: './wine-sales-report.component.html',
  standalone: false,
})
export class WineSalesReportComponent implements OnInit {
 columns: Column[] = [
    { name: 'createdAt', label: '::CreatedOrder', width: '20%', type: 'date' },
    { name: 'paidDate', label: '::PaidDate', width: '20%', type: 'date' },
    { name: 'wineName', label: '::Wine', width: '40%' },
    { name: 'totalBottles', label: '::QuantityBottles', width: '20%' },
  ];

  reportData: WineSalesReportDto[] = [];

  // 📅 Fecha
  dateRange = {
    start: null as Temporal.PlainDate | null,
    end: null as Temporal.PlainDate | null,
  };

  // 🔍 Búsqueda
  searchKeyword: string | null = null;

  // 📅 Período detectado
  detectedPeriod: 'month' | 'year' | null = null;

  // 🔄 Estado
  isLoading = false;
  hasLoadedOnce = false;

  // 📄 Paging
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;

  constructor(
    private reportService: ReportService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Leer query params de la URL usando snapshot para la primera carga
    const params = this.route.snapshot.queryParams;
    const today = Temporal.Now.plainDateISO();

    // Parsear fechas desde query params (formato: DD/MM/YYYY)
    let startDate: Temporal.PlainDate | null = today;
    let endDate: Temporal.PlainDate | null = today;

    if (params['start']) {
      startDate = this.parseDateFromQuery(params['start']);
    }
    if (params['end']) {
      endDate = this.parseDateFromQuery(params['end']);
    }

    this.dateRange = {
      start: startDate,
      end: endDate,
    };

      // Aplicar keyword desde query params
      if (params['search']) {
        this.searchKeyword = params['search'];
      }

      // Determinar período: primero desde query param, si no, detectarlo automáticamente
      if (params['period']) {
        this.detectedPeriod = params['period'] === 'year' ? 'year' : 'month';
      } else if (startDate && endDate) {
        this.detectedPeriod = this.detectPeriodFromDates(startDate, endDate);
      }

      // Cargar reporte con los filtros
      this.loadReport();
  }

  private parseDateFromQuery(dateString: string): Temporal.PlainDate | null {
    try {
      // Formato esperado: DD/MM/YYYY
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        return Temporal.PlainDate.from({ year, month, day });
      }
      // Si viene en formato ISO (YYYY-MM-DD)
      if (dateString.includes('-')) {
        return Temporal.PlainDate.from(dateString);
      }
    } catch (error) {
      console.error('Error parsing date:', error);
    }
    return null;
  }

  private detectPeriodFromDates(start: Temporal.PlainDate, end: Temporal.PlainDate): 'month' | 'year' | null {
    const today = Temporal.Now.plainDateISO();
    
    // Verificar si es período mensual: día 1 del mes actual hasta hoy
    const firstDayOfMonth = Temporal.PlainDate.from({
      year: today.year,
      month: today.month,
      day: 1
    });
    
    if (start.equals(firstDayOfMonth) && end.equals(today)) {
      return 'month';
    }
    
    // Verificar si es período anual: 1 de enero hasta hoy
    const firstDayOfYear = Temporal.PlainDate.from({
      year: today.year,
      month: 1,
      day: 1
    });
    
    if (start.equals(firstDayOfYear) && end.equals(today)) {
      return 'year';
    }
    
    // Si no coincide con ninguno, retornar null
    return null;
  }

  private updateQueryParams(): void {
    const queryParams: any = {};

    if (this.dateRange.start) {
      // Formatear fecha como DD/MM/YYYY para la URL
      const start = this.dateRange.start;
      queryParams['start'] = `${String(start.day).padStart(2, '0')}/${String(start.month).padStart(2, '0')}/${start.year}`;
    }

    if (this.dateRange.end) {
      const end = this.dateRange.end;
      queryParams['end'] = `${String(end.day).padStart(2, '0')}/${String(end.month).padStart(2, '0')}/${end.year}`;
    }

    if (this.searchKeyword) {
      queryParams['search'] = this.searchKeyword;
    }

    // Mantener el período si está detectado
    if (this.detectedPeriod) {
      queryParams['period'] = this.detectedPeriod;
    }

    // Verificar si los query params actuales son diferentes para evitar actualizaciones innecesarias
    const currentParams = this.route.snapshot.queryParams;
    const hasChanges = 
      queryParams['start'] !== currentParams['start'] ||
      queryParams['end'] !== currentParams['end'] ||
      queryParams['search'] !== currentParams['search'];

    if (hasChanges) {
      // Actualizar URL sin recargar la página
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
        skipLocationChange: false
      });
    }
  }

  loadReport(): void {
    if (!this.dateRange.start || !this.dateRange.end) {
      return;
    }

    this.isLoading = true;

    const params: GetWinesSalesReportDto = {
      from: this.dateRange.start.toString(),
      to: this.dateRange.end.toString(),
      keyword: this.searchKeyword || undefined,
      skipCount: (this.currentPage - 1) * this.pageSize,
      maxResultCount: this.pageSize,
    };

    this.reportService.getWineSalesReport(params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.hasLoadedOnce = true;
        })
      )
      .subscribe(result => {
        this.reportData = result.items;
        this.totalCount = result.totalCount;
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadReport();
  }

  onDateRangeChange(range: { start: Temporal.PlainDate; end: Temporal.PlainDate }): void {
    this.dateRange = range;
    this.currentPage = 1;
    this.updateQueryParams();
    this.loadReport();
  }

  onSearch(keyword: string): void {
    this.searchKeyword = keyword;
    this.currentPage = 1;
    this.updateQueryParams();
    this.loadReport();
  }

  onClearSearch(): void {
    this.searchKeyword = null;
    this.currentPage = 1;
    this.updateQueryParams();
    this.loadReport();
  }

  generateWineSalesExcel(): void {
    this.isLoading = true;

    this.reportService
      .generateWineSalesExcel(this.buildFilter(), {
        responseType: 'blob'
      } as any)
      .pipe(
        take(1),
        finalize(() => this.isLoading = false)
      )
      .subscribe(blob => {
        const fileName = this.buildFileName();
        this.downloadExcel(blob, fileName);
      });
  }

  private buildFileName(): string {
    const from = this.dateRange.start?.toString() ?? 'sin-desde';
    const to = this.dateRange.end?.toString() ?? 'sin-hasta';

    return `VentasPorVino_${from}_${to}.xlsx`;
  }

  private downloadExcel(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = fileName;
    a.click();

    window.URL.revokeObjectURL(url);
  }


  private buildFilter(): GetWinesSalesReportDto {
    return {
      from: this.dateRange.start?.toString(),
      to: this.dateRange.end?.toString(),
      keyword: this.searchKeyword || undefined,
      skipCount: 0,
      maxResultCount: 1000
    };
  }

}
